import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Container, Paper, Rating, TextField, Button,
  ToggleButton, ToggleButtonGroup, Alert, Divider, Snackbar,
} from '@mui/material';
import { Star, LocationOn, CalendarToday, Share, ContentCopy } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import eventsService from '../../services/eventsService';
import ReportAbuseModal from '../../components/shared/ReportAbuseModal';
import { colors, designTokens } from '../../theme/theme';

export default function PostEventPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [overall, setOverall] = useState<number | null>(null);
  const [venue, setVenue] = useState<number | null>(null);
  const [again, setAgain] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [showReport, setShowReport] = useState(false);
  const [copySnackbar, setCopySnackbar] = useState(false);

  // Placeholder event data — in production, fetch from API or pass via route state
  const eventTitle = 'CoffeeMates Session';
  const venueName = 'Partner Venue';
  const eventDate = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  const eventUrl = `${window.location.origin}/events/${eventId}`;

  const handleShare = async () => {
    const shareData = {
      title: `${eventTitle} - CoffeeMates`,
      text: `I just attended "${eventTitle}" at ${venueName} and rated it ${overall}/5 stars!`,
      url: eventUrl,
    };
    if (navigator.share && navigator.canShare?.(shareData)) {
      try {
        await navigator.share(shareData);
      } catch {
        // expected: user cancelled the native share dialog — no surfacing needed.
      }
    } else {
      handleCopyLink();
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(eventUrl);
      setCopySnackbar(true);
    } catch (err) {
      console.warn('[PostEventPage] clipboard write failed', err);
      setError('Could not copy link — please copy manually from the address bar');
    }
  };

  const handleSubmit = async () => {
    if (!overall || !eventId) { setError('Please rate your overall experience'); return; }
    try {
      await eventsService.rateEvent(eventId, {
        overall_rating: overall,
        venue_rating: venue || undefined,
        would_attend_again: again || undefined,
        comment: comment || undefined,
      });
      setSubmitted(true);
    } catch (e: any) {
      console.warn('[PostEventPage] rate event failed', e);
      setError(e.message || 'Failed to submit rating');
    }
  };

  if (submitted) {
    return (
      <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="h1" sx={{ fontSize: '4rem', mb: 2 }}>🎉</Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>{t('pages.post_event.thanks')}</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>Your rating helps us make CoffeeMates better.</Typography>

        {/* ── Share Your Experience Card ── */}
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: colors.neutral[700] }}>
          Share Your Experience
        </Typography>
        <Box
          sx={{
            background: `linear-gradient(135deg, ${colors.primary.main}22 0%, ${colors.secondary.main}22 50%, ${colors.primary.dark}33 100%)`,
            border: `1px solid ${designTokens.glass.border}`,
            borderRadius: `${designTokens.radius.lg}px`,
            p: 3,
            mb: 3,
            textAlign: 'left',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: colors.primary.gradient,
            },
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, color: '#FFFFFF' }}>
            {eventTitle}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
            <LocationOn sx={{ fontSize: 18, color: colors.primary.main }} />
            <Typography variant="body2" sx={{ color: colors.neutral[700] }}>
              {venueName}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 2 }}>
            <CalendarToday sx={{ fontSize: 18, color: colors.primary.main }} />
            <Typography variant="body2" sx={{ color: colors.neutral[700] }}>
              {eventDate}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                sx={{
                  fontSize: 24,
                  color: star <= (overall || 0) ? colors.accent.main : colors.neutral[300],
                }}
              />
            ))}
            <Typography variant="body2" sx={{ ml: 1, color: colors.accent.main, fontWeight: 600 }}>
              {overall}/5
            </Typography>
          </Box>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 1.5 }} />
          <Typography variant="caption" sx={{ color: colors.neutral[500], fontWeight: 600, letterSpacing: '0.05em' }}>
            ☕ CoffeeMates
          </Typography>
        </Box>

        {/* ── Share Buttons ── */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<Share />}
            onClick={handleShare}
            sx={{ flex: 1, maxWidth: 200 }}
          >
            Share
          </Button>
          <Button
            variant="outlined"
            startIcon={<ContentCopy />}
            onClick={handleCopyLink}
            sx={{ flex: 1, maxWidth: 200 }}
          >
            Copy Link
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={() => navigate('/')}>Browse Next Week</Button>
          <Button variant="outlined" onClick={() => navigate('/profile/connections')}>My Connections</Button>
        </Box>

        <Snackbar
          open={copySnackbar}
          autoHideDuration={2000}
          onClose={() => setCopySnackbar(false)}
          message="Link copied to clipboard!"
        />
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>{t('pages.post_event.title')}</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>How was your CoffeeMates session?</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Overall Experience *</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Rating value={overall} onChange={(_, v) => setOverall(v)} size="large"
            icon={<Star sx={{ color: colors.accent.main }} fontSize="inherit" />}
            emptyIcon={<Star sx={{ color: colors.neutral[200] }} fontSize="inherit" />} />
          {overall && <Typography variant="h6" sx={{ color: colors.accent.main }}>{overall}/5</Typography>}
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Venue Quality</Typography>
        <Rating value={venue} onChange={(_, v) => setVenue(v)} size="large"
          icon={<Star sx={{ color: colors.secondary.main }} fontSize="inherit" />}
          emptyIcon={<Star sx={{ color: colors.neutral[200] }} fontSize="inherit" />} />
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Would you attend again?</Typography>
        <ToggleButtonGroup value={again} exclusive onChange={(_, v) => setAgain(v)} fullWidth
          sx={{ '& .MuiToggleButton-root': { borderRadius: 2, textTransform: 'none', fontWeight: 600 } }}>
          <ToggleButton value="yes" sx={{ '&.Mui-selected': { bgcolor: colors.success.subtle, color: colors.success.dark } }}>Yes!</ToggleButton>
          <ToggleButton value="maybe" sx={{ '&.Mui-selected': { bgcolor: colors.warning.subtle, color: colors.warning.dark } }}>Maybe</ToggleButton>
          <ToggleButton value="no" sx={{ '&.Mui-selected': { bgcolor: colors.error.subtle, color: colors.error.dark } }}>No</ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Any comments?</Typography>
        <TextField fullWidth multiline rows={3} placeholder="What made this session great (or not)?"
          value={comment} onChange={e => setComment(e.target.value)} inputProps={{ maxLength: 500 }} />
      </Paper>

      <Button fullWidth variant="contained" size="large" sx={{ py: 1.5, mb: 2 }} onClick={handleSubmit}>
        Submit Rating
      </Button>

      <Button fullWidth variant="outlined" color="error" size="small" onClick={() => setShowReport(true)}>
        Report an Issue
      </Button>

      {eventId && (
        <ReportAbuseModal open={showReport} onClose={() => setShowReport(false)} eventId={eventId} />
      )}
    </Container>
  );
}
